import {
  type HTMLAttributes,
  type Key,
  useEffect,
  useMemo,
  useState,
} from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  CandidacyAssignmentRulesQuery,
  OrgTeamMembersQuery,
  useCandidacyAssignmentRulesQuery,
  useCreateCandidacyAssignmentRuleMutation,
  useDeleteCandidacyAssignmentRuleMutation,
  useOrgTeamMembersQuery,
  useSearchFhirLocationsLazyQuery,
  useSearchFhirOrganizationsLazyQuery,
  useTestCandidacyAutoAssignmentLazyQuery,
  useUpdateCandidacyAssignmentRuleMutation,
} from "~/graphql/__generated__/graphql";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";
import useDebouncedState from "~/hooks/useDebouncedState";

type AssignmentRule =
  CandidacyAssignmentRulesQuery["candidacyAssignmentRules"][number];
type TeamMember = OrgTeamMembersQuery["organizationUser"][number];

type FhirOption = {
  fhirId: string;
  displayText: string;
  name: string;
  hierarchy: string[];
  typeDisplay?: string | null;
  formDisplay?: string | null;
  managingOrganization?: string | null;
};

type AssigneeOption = {
  id: number;
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
};

type RuleFormState = {
  fhirOrganization: FhirOption | null;
  fhirLocation: FhirOption | null;
  assignedTo: AssigneeOption | null;
};

interface AssignmentRuleManagerProps {
  tableMaxHeight?: string;
}

const SEARCH_DEBOUNCE_MS = 200;
const SEARCH_LIMIT = 20;
const MIN_SEARCH_INPUT_LENGTH = 2;
const REFETCH_QUERIES = ["CandidacyAssignmentRules"];
const FHIR_ID_PREFIX_PATTERN = /^(loc|location|org|organization)[-_]+/i;

const EMPTY_FORM: RuleFormState = {
  fhirOrganization: null,
  fhirLocation: null,
  assignedTo: null,
};

const normalizeText = (value: string | null | undefined) =>
  (value ?? "").replace(/\s+/g, " ").trim();

const hasSearchInput = (value: string) =>
  value.trim().length >= MIN_SEARCH_INPUT_LENGTH;

const titleCase = (value: string) =>
  value
    .split(" ")
    .map((word) => {
      if (/^[a-z]\d+$/i.test(word)) return word.toUpperCase();
      return word ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}` : "";
    })
    .join(" ");

const humanizeFhirId = (fhirId: string) =>
  titleCase(
    normalizeText(
      fhirId
        .replace(FHIR_ID_PREFIX_PATTERN, "")
        .replace(/[_-]+/g, " "),
    ),
  );

const getFhirPrimaryLabel = (option: FhirOption | null | undefined) => {
  if (!option) return "";
  const name = normalizeText(option.name);
  const hierarchyName = normalizeText(option.hierarchy.at(-1));
  const idLabel = humanizeFhirId(option.fhirId);
  return name || hierarchyName || idLabel || option.fhirId;
};

const getFhirLabel = (option: FhirOption | null | undefined) =>
  getFhirPrimaryLabel(option) || option?.fhirId || "";

const getFhirHierarchy = (option: FhirOption | null | undefined) => {
  if (!option) return [];
  const hierarchy = option.hierarchy.map(normalizeText).filter(Boolean);
  return hierarchy.length > 0 ? hierarchy : [getFhirPrimaryLabel(option)];
};

const getFhirHierarchyLabel = (option: FhirOption) =>
  getFhirHierarchy(option).join(" -> ");

const getFhirMetaLabels = (option: FhirOption) =>
  Array.from(
    new Set(
      [
        option.formDisplay,
        option.typeDisplay,
        option.managingOrganization,
      ].map(normalizeText).filter(Boolean),
    ),
  );

const getFhirDisplayTextLabel = (option: FhirOption) => {
  const displayText = normalizeText(option.displayText);
  if (!displayText) return "";

  const visibleLabels = new Set([
    normalizeText(getFhirPrimaryLabel(option)),
    normalizeText(getFhirHierarchyLabel(option)),
    normalizeText(option.fhirId),
  ]);

  return visibleLabels.has(displayText) ? "" : displayText;
};

const getAssigneeLabel = (option: AssigneeOption | null | undefined) => {
  if (!option) return "";
  const name =
    `${option.user?.firstName ?? ""} ${option.user?.lastName ?? ""}`.trim();
  return name || option.user?.email || `User ${option.id}`;
};

const toFhirOption = (
  value: FhirOption | null | undefined,
  fallbackId?: string | null,
): FhirOption | null => {
  if (value) return value;
  return fallbackId
    ? {
        fhirId: fallbackId,
        displayText: fallbackId,
        name: humanizeFhirId(fallbackId) || fallbackId,
        hierarchy: [],
        typeDisplay: null,
        formDisplay: null,
        managingOrganization: null,
      }
    : null;
};

const toAssigneeOption = (
  value: AssignmentRule["assignedTo"] | TeamMember | null | undefined,
): AssigneeOption | null => {
  if (!value) return null;
  return {
    id: value.id,
    user: value.user
      ? {
          firstName: value.user.firstName,
          lastName: value.user.lastName,
          email: "email" in value.user ? value.user.email : null,
        }
      : null,
  };
};

const toFhirOptions = (
  options: readonly FhirOption[],
  selected: FhirOption | null,
) => {
  const result = [...options];
  return !selected || result.some((option) => option.fhirId === selected.fhirId)
    ? result
    : [selected, ...result];
};

const renderFhirOption = (
  props: HTMLAttributes<HTMLLIElement> & { key: Key },
  option: FhirOption,
) => {
  const { key, ...optionProps } = props;
  const hierarchyLabel = getFhirHierarchyLabel(option);
  const showHierarchy =
    Boolean(hierarchyLabel) && hierarchyLabel !== getFhirPrimaryLabel(option);
  const metaLabels = getFhirMetaLabels(option);
  const displayTextLabel = getFhirDisplayTextLabel(option);

  return (
    <Box
      key={key}
      component="li"
      {...optionProps}
      sx={{
        display: "block",
        py: 1,
        px: 1.25,
        minHeight: "auto",
      }}
    >
      <Stack spacing={0.75} sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.35,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
          }}
        >
          {getFhirPrimaryLabel(option)}
        </Typography>
        {showHierarchy ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
          >
            {hierarchyLabel}
          </Typography>
        ) : null}
        {displayTextLabel ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
          >
            {displayTextLabel}
          </Typography>
        ) : null}
        {metaLabels.length > 0 ? (
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {metaLabels.map((label) => (
              <Chip
                key={label}
                size="small"
                label={label}
                sx={{
                  height: 22,
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    display: "block",
                    fontSize: 11,
                    overflow: "hidden",
                    px: 0.75,
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ))}
          </Stack>
        ) : null}
        <Chip
          size="small"
          variant="outlined"
          label={option.fhirId}
          sx={{
            alignSelf: "flex-start",
            height: 22,
            maxWidth: "100%",
            bgcolor: "background.paper",
            "& .MuiChip-label": {
              display: "block",
              fontFamily: "monospace",
              fontSize: 11,
              overflow: "hidden",
              px: 0.75,
              textOverflow: "ellipsis",
            },
          }}
        />
      </Stack>
    </Box>
  );
};

const renderFhirValue = (
  option: FhirOption | null | undefined,
  emptyLabel: string,
) => {
  if (!option) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  const hierarchy = getFhirHierarchy(option);
  const displayTextLabel = getFhirDisplayTextLabel(option);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
        }}
      >
        {getFhirPrimaryLabel(option)}
      </Typography>
      {hierarchy.length > 1 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {hierarchy.join(" -> ")}
        </Typography>
      ) : null}
      {displayTextLabel ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayTextLabel}
        </Typography>
      ) : null}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          fontFamily: "monospace",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option.fhirId}
      </Typography>
    </Box>
  );
};

const renderAssigneeValue = (
  assignee: AssignmentRule["assignedTo"] | null | undefined,
) => {
  const email = assignee?.user?.email;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {getAssigneeLabel(assignee)}
      </Typography>
      {email ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {email}
        </Typography>
      ) : null}
    </Box>
  );
};

const getRuleMatchLabel = (
  rule: Pick<AssignmentRule, "fhirLocationId" | "fhirOrganizationId">,
) => {
  const hasLocation = Boolean(rule.fhirLocationId);
  const hasDepartment = Boolean(rule.fhirOrganizationId);

  if (hasLocation && hasDepartment) return "Department + location";
  if (hasDepartment) return "Department";
  if (hasLocation) return "Location";
  return "Condition missing";
};

const fhirAutocompleteSlotProps = {
  popper: {
    sx: {
      minWidth: { xs: "calc(100vw - 32px)", sm: 420 },
      maxWidth: { xs: "calc(100vw - 32px)", sm: 560 },
    },
  },
  paper: {
    sx: {
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      boxShadow: 8,
    },
  },
  listbox: {
    sx: {
      maxHeight: 360,
      p: 0.5,
      "& .MuiAutocomplete-option": {
        alignItems: "initial",
        borderRadius: 0.75,
        display: "block",
      },
      "& .MuiAutocomplete-option + .MuiAutocomplete-option": {
        mt: 0.25,
      },
    },
  },
};

type FhirAutocompleteProps = {
  label: string;
  value: FhirOption | null;
  inputValue: string;
  options: FhirOption[];
  loading: boolean;
  errorMessage?: string;
  gridColumn?: {
    xs: string;
    md: string;
    lg: string;
    xl?: string;
  };
  setInputValue: (value: string) => void;
  onChange: (value: FhirOption | null) => void;
};

const inputEndAdornment = (
  params: AutocompleteRenderInputParams,
  loading: boolean,
) => (
  <>
    {loading ? <CircularProgress color="inherit" size={16} /> : null}
    {params.InputProps.endAdornment}
  </>
);

const FhirAutocomplete = ({
  label,
  value,
  inputValue,
  options,
  loading,
  errorMessage,
  gridColumn = { xs: "1", md: "span 1", lg: "span 6" },
  setInputValue,
  onChange,
}: FhirAutocompleteProps) => {
  const hasPendingSearch =
    hasSearchInput(inputValue) && inputValue !== getFhirLabel(value);

  return (
    <Autocomplete<FhirOption>
      sx={{
        gridColumn,
        minWidth: 0,
        "& .MuiAutocomplete-popupIndicator": {
          display: "none",
        },
      }}
      options={options}
      open={hasPendingSearch}
      value={value}
      inputValue={inputValue}
      forcePopupIcon={false}
      onInputChange={(_event, nextValue) => {
        setInputValue(nextValue);
      }}
      onChange={(_event, nextValue) => onChange(nextValue)}
      getOptionLabel={getFhirLabel}
      isOptionEqualToValue={(option, selected) =>
        option.fhirId === selected.fhirId
      }
      filterOptions={(items) => items}
      loading={loading}
      loadingText={`Searching ${label.toLowerCase()}s...`}
      noOptionsText={`No matching ${label.toLowerCase()}s`}
      renderOption={renderFhirOption}
      slotProps={fhirAutocompleteSlotProps}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Type 2+ chars, name or ID"
          error={Boolean(errorMessage)}
          helperText={
            errorMessage ?? `Type at least 2 characters to search ${label.toLowerCase()}s.`
          }
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: inputEndAdornment(params, loading),
            },
          }}
        />
      )}
    />
  );
};

type UpdateRuleForm = <TKey extends keyof RuleFormState>(
  key: TKey,
  value: RuleFormState[TKey],
) => void;

type RuleEditorProps = {
  editingId: number | null;
  form: RuleFormState;
  organizationOptions: FhirOption[];
  locationOptions: FhirOption[];
  organizationInput: string;
  locationInput: string;
  organizationLoading: boolean;
  locationLoading: boolean;
  organizationErrorMessage?: string;
  locationErrorMessage?: string;
  teamOptions: AssigneeOption[];
  teamLoading: boolean;
  saving: boolean;
  mutating: boolean;
  canSave: boolean;
  setOrganizationInput: (value: string) => void;
  setLocationInput: (value: string) => void;
  updateForm: UpdateRuleForm;
  onSave: () => void;
};

const RuleEditor = ({
  editingId,
  form,
  organizationOptions,
  locationOptions,
  organizationInput,
  locationInput,
  organizationLoading,
  locationLoading,
  organizationErrorMessage,
  locationErrorMessage,
  teamOptions,
  teamLoading,
  saving,
  mutating,
  canSave,
  setOrganizationInput,
  setLocationInput,
  updateForm,
  onSave,
}: RuleEditorProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "minmax(0, 1fr) minmax(0, 1fr)",
        lg: "repeat(12, minmax(0, 1fr))",
      },
      gap: 2,
      alignItems: "flex-start",
      p: 2,
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      bgcolor: "background.default",
    }}
  >
    <Box
      sx={{
        gridColumn: "1 / -1",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {editingId ? "Edit rule" : "New rule"}
      </Typography>
      {editingId ? (
        <Chip size="small" color="primary" label={`Rule #${editingId}`} />
      ) : null}
    </Box>
    <FhirAutocomplete
      label="Department"
      gridColumn={{ xs: "1", md: "span 1", lg: "span 6", xl: "span 4" }}
      options={organizationOptions}
      value={form.fhirOrganization}
      inputValue={organizationInput}
      loading={organizationLoading}
      errorMessage={organizationErrorMessage}
      setInputValue={setOrganizationInput}
      onChange={(value) => updateForm("fhirOrganization", value)}
    />
    <FhirAutocomplete
      label="Location"
      gridColumn={{ xs: "1", md: "span 1", lg: "span 6", xl: "span 4" }}
      options={locationOptions}
      value={form.fhirLocation}
      inputValue={locationInput}
      loading={locationLoading}
      errorMessage={locationErrorMessage}
      setInputValue={setLocationInput}
      onChange={(value) => updateForm("fhirLocation", value)}
    />
    <Autocomplete<AssigneeOption>
      sx={{
        gridColumn: { xs: "1", md: "span 1", lg: "span 9", xl: "span 2" },
        minWidth: 0,
      }}
      options={teamOptions}
      value={form.assignedTo}
      onChange={(_event, value) => updateForm("assignedTo", value)}
      getOptionLabel={getAssigneeLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={teamLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          label="Assign to"
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: inputEndAdornment(params, teamLoading),
            },
          }}
        />
      )}
    />
    <Box
      sx={{
        gridColumn: { xs: "1", md: "span 1", lg: "span 3", xl: "span 2" },
        display: "flex",
        justifyContent: { xs: "stretch", md: "flex-end" },
      }}
    >
      <Button
        variant="contained"
        startIcon={
          saving ? (
            <CircularProgress color="inherit" size={16} />
          ) : editingId ? (
            <SaveIcon />
          ) : (
            <AddIcon />
          )
        }
        onClick={onSave}
        disabled={!canSave || mutating}
        fullWidth
        sx={{
          height: 40,
          maxWidth: { md: 180 },
          minWidth: 0,
          whiteSpace: "nowrap",
        }}
      >
        {saving ? "Saving" : editingId ? "Save" : "Add"}
      </Button>
    </Box>
  </Box>
);

type RulesTableProps = {
  rules: AssignmentRule[];
  editingId: number | null;
  loading: boolean;
  mutating: boolean;
  tableMaxHeight: string;
  onEdit: (rule: AssignmentRule) => void;
  onDelete: (rule: AssignmentRule) => void;
};

const RulesTable = ({
  rules,
  editingId,
  loading,
  mutating,
  tableMaxHeight,
  onEdit,
  onDelete,
}: RulesTableProps) => (
  <Box sx={{ minHeight: 160 }}>
    {loading ? (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    ) : rules.length === 0 ? (
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          px: 2,
          py: 3,
        }}
      >
        <Typography color="text.secondary">
          No assignment rules configured.
        </Typography>
      </Box>
    ) : (
      <TableContainer
        component={Box}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          maxHeight: tableMaxHeight,
          overflow: "auto",
        }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label="Assignment rules"
          sx={{
            tableLayout: "fixed",
            minWidth: 1080,
            "& th": {
              bgcolor: "background.default",
              fontWeight: 600,
            },
            "& td, & th": {
              px: 2,
              py: 1.5,
              verticalAlign: "top",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 156 }}>Match</TableCell>
              <TableCell sx={{ width: "28%" }}>Department</TableCell>
              <TableCell sx={{ width: "28%" }}>Location</TableCell>
              <TableCell sx={{ width: "20%" }}>Assign to</TableCell>
              <TableCell sx={{ width: 116 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule) => (
              <TableRow key={rule.id} selected={rule.id === editingId}>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={getRuleMatchLabel(rule)}
                  />
                </TableCell>
                <TableCell>
                  {renderFhirValue(
                    toFhirOption(rule.fhirOrganization, rule.fhirOrganizationId),
                    "Any department",
                  )}
                </TableCell>
                <TableCell>
                  {renderFhirValue(
                    toFhirOption(rule.fhirLocation, rule.fhirLocationId),
                    "Any location",
                  )}
                </TableCell>
                <TableCell>{renderAssigneeValue(rule.assignedTo)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit rule" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(rule)}
                      disabled={mutating}
                      aria-label="Edit rule"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete rule" arrow>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(rule)}
                      disabled={mutating}
                      aria-label="Delete rule"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

type DeleteRuleDialogProps = {
  rule: AssignmentRule | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

type TestAssignmentDialogProps = {
  open: boolean;
  encounterJson: string;
  orgUserId: number | null | undefined;
  teamOptions: AssigneeOption[];
  errorMessage: string | null;
  loading: boolean;
  onClose: () => void;
  onEncounterJsonChange: (value: string) => void;
  onSubmit: () => void;
};

const TestAssignmentDialog = ({
  open,
  encounterJson,
  orgUserId,
  teamOptions,
  errorMessage,
  loading,
  onClose,
  onEncounterJsonChange,
  onSubmit,
}: TestAssignmentDialogProps) => {
  const matchedAssignee =
    orgUserId == null
      ? null
      : teamOptions.find((option) => option.id === orgUserId) ?? {
          id: orgUserId,
          user: null,
        };

  return (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Test auto-assignment</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Paste a FHIR Encounter resource as JSON. The current organization&apos;s
          assignment rules will be evaluated against its location and department
          references.
        </Typography>
        <TextField
          label="Encounter resource JSON"
          value={encounterJson}
          onChange={(event) => onEncounterJsonChange(event.target.value)}
          multiline
          minRows={10}
          fullWidth
          size="small"
          slotProps={{
            input: {
              sx: { fontFamily: "monospace", fontSize: "0.85rem" },
            },
          }}
        />
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {orgUserId !== undefined ? (
          <Alert severity={orgUserId ? "success" : "info"}>
            {orgUserId ? (
              <>
                Matched assignee:{" "}
                <strong>{getAssigneeLabel(matchedAssignee)}</strong>
              </>
            ) : (
              "No matching assignee."
            )}
          </Alert>
        ) : null}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Close
      </Button>
      <Button
        variant="contained"
        onClick={onSubmit}
        disabled={loading || !encounterJson.trim()}
      >
        {loading ? "Testing..." : "Submit"}
      </Button>
    </DialogActions>
  </Dialog>
  );
};

const DeleteRuleDialog = ({
  rule,
  loading,
  onClose,
  onConfirm,
}: DeleteRuleDialogProps) => (
  <Dialog open={!!rule} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Delete assignment rule?</DialogTitle>
    <DialogContent dividers>
      {rule ? (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            This removes the rule from future automatic assignment.
          </Typography>
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              {getRuleMatchLabel(rule)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {getAssigneeLabel(rule.assignedTo)}
            </Typography>
          </Box>
        </Stack>
      ) : null}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        color="error"
        variant="contained"
        onClick={onConfirm}
        disabled={loading}
        startIcon={
          loading ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            <DeleteIcon />
          )
        }
      >
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

const AssignmentRuleManager = ({
  tableMaxHeight = "min(52vh, 560px)",
}: AssignmentRuleManagerProps) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteCandidate, setDeleteCandidate] =
    useState<AssignmentRule | null>(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEncounterJson, setTestEncounterJson] = useState("");
  const [testResult, setTestResult] = useState<number | null | undefined>(
    undefined,
  );
  const [testErrorMessage, setTestErrorMessage] = useState<string | null>(null);

  const [
    locationInput,
    debouncedLocationInput,
    setLocationInput,
  ] = useDebouncedState("", SEARCH_DEBOUNCE_MS);
  const [
    organizationInput,
    debouncedOrganizationInput,
    setOrganizationInput,
  ] = useDebouncedState("", SEARCH_DEBOUNCE_MS);

  const { data, loading, error } = useCandidacyAssignmentRulesQuery({
    fetchPolicy: "cache-and-network",
  });
  const { data: teamData, loading: teamLoading } = useOrgTeamMembersQuery();
  const [
    searchLocations,
    {
      data: locationData,
      loading: locationLoading,
      error: locationSearchError,
    },
  ] = useSearchFhirLocationsLazyQuery();
  const [
    searchOrganizations,
    {
      data: organizationData,
      loading: organizationLoading,
      error: organizationSearchError,
    },
  ] = useSearchFhirOrganizationsLazyQuery();

  const [createRule, { loading: createLoading }] =
    useCreateCandidacyAssignmentRuleMutation({
      refetchQueries: REFETCH_QUERIES,
      awaitRefetchQueries: true,
    });
  const [updateRule, { loading: updateLoading }] =
    useUpdateCandidacyAssignmentRuleMutation({
      refetchQueries: REFETCH_QUERIES,
      awaitRefetchQueries: true,
    });
  const [deleteRule, { loading: deleteLoading }] =
    useDeleteCandidacyAssignmentRuleMutation({
      refetchQueries: REFETCH_QUERIES,
      awaitRefetchQueries: true,
    });
  const [runAutoAssignmentTest, { loading: testLoading }] =
    useTestCandidacyAutoAssignmentLazyQuery({
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    const keyword = debouncedLocationInput.trim();
    if (hasSearchInput(keyword)) {
      searchLocations({ variables: { keyword, limit: SEARCH_LIMIT } });
    }
  }, [debouncedLocationInput, searchLocations]);

  useEffect(() => {
    const keyword = debouncedOrganizationInput.trim();
    if (hasSearchInput(keyword)) {
      searchOrganizations({ variables: { keyword, limit: SEARCH_LIMIT } });
    }
  }, [debouncedOrganizationInput, searchOrganizations]);

  const rules = data?.candidacyAssignmentRules ?? [];
  const teamOptions = useMemo(
    () =>
      (teamData?.organizationUser ?? [])
        .map(toAssigneeOption)
        .filter((option): option is AssigneeOption => Boolean(option)),
    [teamData?.organizationUser],
  );
  const locationOptions = useMemo(
    () =>
      toFhirOptions(locationData?.searchFhirLocations ?? [], form.fhirLocation),
    [form.fhirLocation, locationData?.searchFhirLocations],
  );
  const organizationOptions = useMemo(
    () =>
      toFhirOptions(
        organizationData?.searchFhirOrganizations ?? [],
        form.fhirOrganization,
      ),
    [form.fhirOrganization, organizationData?.searchFhirOrganizations],
  );

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrorMessage(null);
    setDeleteCandidate(null);
    setLocationInput("");
    setOrganizationInput("");
  };

  const updateForm = <TKey extends keyof RuleFormState>(
    key: TKey,
    value: RuleFormState[TKey],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleEdit = (rule: AssignmentRule) => {
    const fhirOrganization = toFhirOption(
      rule.fhirOrganization,
      rule.fhirOrganizationId,
    );
    const fhirLocation = toFhirOption(rule.fhirLocation, rule.fhirLocationId);

    setEditingId(rule.id);
    setForm({
      fhirOrganization,
      fhirLocation,
      assignedTo: toAssigneeOption(rule.assignedTo),
    });
    setOrganizationInput(getFhirLabel(fhirOrganization));
    setLocationInput(getFhirLabel(fhirLocation));
    setErrorMessage(null);
  };

  const handleSave = async () => {
    if (!form.assignedTo) return;

    try {
      setErrorMessage(null);
      const variables = {
        fhirLocationId: form.fhirLocation?.fhirId ?? null,
        fhirOrganizationId: form.fhirOrganization?.fhirId ?? null,
        assignedToId: form.assignedTo.id,
      };

      if (editingId) {
        await updateRule({ variables: { id: editingId, ...variables } });
      } else {
        await createRule({ variables });
      }
      resetForm();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Unable to save rule");
    }
  };

  const handleOpenTestDialog = () => {
    setTestDialogOpen(true);
    setTestResult(undefined);
    setTestErrorMessage(null);
  };

  const handleCloseTestDialog = () => {
    if (testLoading) return;
    setTestDialogOpen(false);
  };

  const handleSubmitAutoAssignmentTest = async () => {
    try {
      setTestErrorMessage(null);
      setTestResult(undefined);
      const { data: testData, error: testQueryError } =
        await runAutoAssignmentTest({
          variables: { encounterResourceJson: testEncounterJson },
        });

      if (testQueryError) {
        setTestErrorMessage(testQueryError.message);
        return;
      }

      setTestResult(testData?.testCandidacyAutoAssignment ?? null);
    } catch (err) {
      setTestErrorMessage(
        err instanceof Error ? err.message : "Unable to test auto-assignment",
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return;

    try {
      setErrorMessage(null);
      await deleteRule({ variables: { id: deleteCandidate.id } });
      if (editingId === deleteCandidate.id) {
        resetForm();
      } else {
        setDeleteCandidate(null);
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Unable to delete rule",
      );
      setDeleteCandidate(null);
    }
  };

  const saving = createLoading || updateLoading;
  const mutating = saving || deleteLoading;
  const canSave =
    !!form.assignedTo && (!!form.fhirLocation || !!form.fhirOrganization);
  return (
    <>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
            Assignment Rules
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {!loading ? (
              <Chip
                size="small"
                variant="outlined"
                label={`${rules.length} ${rules.length === 1 ? "rule" : "rules"}`}
              />
            ) : null}
            <PermissionOrHide permission={Permission.debugApps}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleOpenTestDialog}
                disabled={mutating}
              >
                Test
              </Button>
            </PermissionOrHide>
          </Stack>
        </Stack>
        {error ? (
          <Alert severity="error">
            Failed to load assignment rules: {error.message}
          </Alert>
        ) : null}
        {errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : null}

        <RuleEditor
          editingId={editingId}
          form={form}
          organizationOptions={organizationOptions}
          locationOptions={locationOptions}
          organizationInput={organizationInput}
          locationInput={locationInput}
          organizationLoading={organizationLoading}
          locationLoading={locationLoading}
          organizationErrorMessage={organizationSearchError?.message}
          locationErrorMessage={locationSearchError?.message}
          teamOptions={teamOptions}
          teamLoading={teamLoading}
          saving={saving}
          mutating={mutating}
          canSave={canSave}
          setOrganizationInput={setOrganizationInput}
          setLocationInput={setLocationInput}
          updateForm={updateForm}
          onSave={handleSave}
        />
        <RulesTable
          rules={rules}
          editingId={editingId}
          loading={loading}
          mutating={mutating}
          tableMaxHeight={tableMaxHeight}
          onEdit={handleEdit}
          onDelete={setDeleteCandidate}
        />
        {editingId ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={resetForm} disabled={mutating}>
              Cancel edit
            </Button>
          </Stack>
        ) : null}
      </Stack>
      <DeleteRuleDialog
        rule={deleteCandidate}
        loading={deleteLoading}
        onClose={() => {
          if (!deleteLoading) setDeleteCandidate(null);
        }}
        onConfirm={handleConfirmDelete}
      />
      <PermissionOrHide permission={Permission.debugApps}>
        <TestAssignmentDialog
          open={testDialogOpen}
          encounterJson={testEncounterJson}
          orgUserId={testResult}
          teamOptions={teamOptions}
          errorMessage={testErrorMessage}
          loading={testLoading}
          onClose={handleCloseTestDialog}
          onEncounterJsonChange={setTestEncounterJson}
          onSubmit={handleSubmitAutoAssignmentTest}
        />
      </PermissionOrHide>
    </>
  );
};

export default AssignmentRuleManager;
